import { NoteTopicId, Relations } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { AdminNoteTopic } from "./types";

export const useDeleteAdminNoteTopic = () => {
	const queryClient = useQueryClient();
	const { _delete } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationFn: async ({ noteTopicId }: { noteTopicId: NoteTopicId; relation: Relations.Notes }) =>
			_delete(`/v2/admin/admin-notes/note-topics/${noteTopicId}`, undefined, "none"),
		onMutate: async ({ noteTopicId, relation }) => {
			await queryClient.cancelQueries({
				queryKey: adminNoteTopicsQueryKey(relation),
			});
			const previousTopics = queryClient.getQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
			);
			queryClient.setQueryData<AdminNoteTopic[]>(adminNoteTopicsQueryKey(relation), (old) => {
				if (!old) return [];
				return old.reduce<AdminNoteTopic[]>((acc, topic) => {
					if (topic.id === noteTopicId) {
						if (topic.summary === "" && topic.notes.length === 0) {
							return acc;
						}
						acc.push({ ...topic, isArchived: true });
					} else {
						acc.push(topic);
					}
					return acc;
				}, []);
			});
			return { previousTopics, relation };
		},
		onError: (error: Error, _inputData, context) => {
			if (context?.previousTopics !== undefined) {
				queryClient.setQueryData(adminNoteTopicsQueryKey(context.relation), context.previousTopics);
			}
			triggerErrorModal(error);
		},
	});

	return {
		deleteAdminNoteTopic: mutation.mutate,
		deleteAdminNoteTopicIsLoading: mutation.isPending,
	};
};
